import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getConsent, setConsent, initAnalytics, stopAnalytics, CONSENT_KEY } from "../lib/analytics";

// Window-level event so Footer.jsx's "Cookie settings" button can re-open the
// banner even though it isn't a descendant of ConsentBanner in the tree.
const REOPEN_EVENT = "grassurn:consent-reopen";

export function openConsentSettings() {
  try {
    window.localStorage.removeItem(CONSENT_KEY);
  } catch (err) {
    // Storage unavailable — the banner still re-shows via the event below.
  }
  window.dispatchEvent(new Event(REOPEN_EVENT));
}

const ConsentBanner = () => {
  const [visible, setVisible] = useState(() => getConsent() === null);

  // A returning visitor who already accepted gets tracked again on this visit.
  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    const handleReopen = () => setVisible(true);
    window.addEventListener(REOPEN_EVENT, handleReopen);
    return () => window.removeEventListener(REOPEN_EVENT, handleReopen);
  }, []);

  if (!visible) return null;

  const handleAccept = () => {
    setConsent("accepted");
    initAnalytics();
    setVisible(false);
  };

  const handleDecline = () => {
    setConsent("rejected");
    // Stops capturing immediately if analytics were already running this
    // page view (e.g. Accept, then reopen via Footer's Cookie settings and
    // Decline) — otherwise PostHog keeps recording until the next reload.
    stopAnalytics();
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label="Analytics consent"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        background: "#0A1613",
        borderTop: "1px solid rgba(91,148,144,0.24)",
        padding: "20px 24px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
      }}
    >
      <p
        style={{
          fontFamily: "Montserrat, sans-serif",
          fontSize: 13,
          fontWeight: 300,
          lineHeight: 1.5,
          color: "rgba(255,255,255,0.80)",
          margin: 0,
          flex: "1 1 320px",
        }}
      >
        We use analytics cookies to understand how visitors use this site — session
        recordings mask every form field, and nothing runs until you accept. Read our{" "}
        <Link to="/privacy" style={{ color: "#7BB5B1", textDecoration: "underline" }}>
          Privacy Policy
        </Link>{" "}
        for details.
      </p>

      <div style={{ display: "flex", gap: "12px", flexShrink: 0 }}>
        <button
          type="button"
          onClick={handleDecline}
          style={{
            fontFamily: "Montserrat, sans-serif",
            fontSize: 13,
            fontWeight: 500,
            color: "rgba(255,255,255,0.72)",
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.24)",
            borderRadius: 6,
            padding: "10px 18px",
            cursor: "pointer",
          }}
        >
          Decline
        </button>
        <button
          type="button"
          onClick={handleAccept}
          style={{
            fontFamily: "Montserrat, sans-serif",
            fontSize: 13,
            fontWeight: 600,
            color: "#04100D",
            background: "#7BB5B1",
            border: "none",
            borderRadius: 6,
            padding: "10px 18px",
            cursor: "pointer",
          }}
        >
          Accept analytics
        </button>
      </div>
    </div>
  );
};

export default ConsentBanner;
